import React, { CSSProperties, useMemo, useRef, useState } from 'react';
import { useSlate } from 'slate-react';
import { ButtonGroup } from 'components/_new/ButtonGroup/ButtonGroup';
import {
	AligmentFormat,
	BaseBlockFormat,
	EmbedBlockFormat,
	FloatFormat,
	ListFormat,
	MarkFormat,
	TableFormat,
} from 'components/_new/NewSlateEditor/types';
import {
	toggleBlock,
	toggleMark,
	isMarkActive,
	addMarkData,
	isBlockActive,
	activeMarkBoolean,
	insertBlock,
	activeMarkValue,
} from './utils/SlateUtilityFunctions';
import { DropdownSimple } from '../Dropdown/DropdownSimple';
import { ReactComponent as HeadingOne } from './assets/headingOne.svg';
import { ReactComponent as HeadingTwo } from './assets/headingTwo.svg';
import { ReactComponent as HeadingThree } from './assets/headingThree.svg';
import { ReactComponent as FontSize } from './assets/fontSize.svg';
import { InsertEmbedPopup } from './Elements/Embed/InsertEmbedPopup';
import { InsertLinkPopup } from './Elements/Link/InsertLinkPopup';
import { OverlayElement } from '../Overlay';
import { Button } from '../Button';
import { TableSelector } from './Elements/Table/TableSelectorPopup';
import { ColorPickerPopup } from './Elements/Color Picker/ColorPickerPopup';
import { isDarkColor, isLightColor } from '../../../utils/color';

type ToolbarProps = {
	uploadRequestFunction?: ((data: FormData) => Promise<string>) | undefined;
};

const Toolbar = ({ uploadRequestFunction }: ToolbarProps) => {
	const editor = useSlate();

	const colorPickerPopupRef = useRef<OverlayElement>(null);
	const backgroundColorPickerPopupRef = useRef<OverlayElement>(null);
	const imageEmbedRef = useRef<OverlayElement>(null);
	const videoEmbedRef = useRef<OverlayElement>(null);
	const linkPopupRef = useRef<OverlayElement>(null);
	const tablePopupRef = useRef<OverlayElement>(null);
	const [whileInsertingState, setWhileInsertingState] = useState({
		color: false,
		backgroundColor: false,
		image: false,
		video: false,
		link: false,
		table: false,
	});

	const currentColor = activeMarkValue(editor, MarkFormat.COLOR);
	const colorButtonStyle: CSSProperties = useMemo(() => {
		const color = currentColor || '';
		const isLight = currentColor && isLightColor(currentColor);
		return {
			color,
			textShadow: isLight ? '1px 1px 0px black, 0px 0px 2px black' : undefined,
		};
	}, [currentColor]);

	const currentBackgroundColor = activeMarkValue(editor, MarkFormat.BACKGROUND_COLOR);
	const backgroundColorButtonStyle: CSSProperties = useMemo(() => {
		// const color = currentBackgroundColor || '';
		const isDark = currentBackgroundColor && isDarkColor(currentBackgroundColor);
		return {
			color: isDark ? 'white' : undefined,
			backgroundColor: currentBackgroundColor,
		};
	}, [currentBackgroundColor]);

	return (
		<div className="toolbar flex flex-row gap-2 flex-wrap align-items-center">
			<DropdownSimple
				value={activeMarkValue(editor, MarkFormat.FONT_SIZE)}
				onChange={(value) => {
					addMarkData(editor, MarkFormat.FONT_SIZE, String(value));
				}}
				options={[
					{ label: 'Mały', value: 'small' },
					{ label: 'Standardowy', value: 'normal' },
					{ label: 'Średni', value: 'medium' },
					{ label: 'Duży', value: 'large' },
				]}
				variant="primary-outlined"
				renderLeft={() => <FontSize height={20} />}
			/>

			<ColorPickerPopup
				ref={colorPickerPopupRef}
				format={MarkFormat.COLOR}
				editor={editor}
				onOpenStateChange={(isOpen) => setWhileInsertingState((prev) => ({ ...prev, color: isOpen }))}
			/>
			<ColorPickerPopup
				ref={backgroundColorPickerPopupRef}
				format={MarkFormat.BACKGROUND_COLOR}
				editor={editor}
				onOpenStateChange={(isOpen) => setWhileInsertingState((prev) => ({ ...prev, backgroundColor: isOpen }))}
			/>
			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: MarkFormat.COLOR,
						label: 'Kolor czcionki',
						icon: 'font',
						onMouseDown: (event) => {
							event.preventDefault();
							colorPickerPopupRef?.current?.toggle(event?.currentTarget);
						},
						style: colorButtonStyle,
					},
					{
						key: MarkFormat.BACKGROUND_COLOR,
						label: 'Kolor tła',
						icon: 'palette',
						onMouseDown: (event) => {
							event.preventDefault();
							backgroundColorPickerPopupRef?.current?.toggle(event?.currentTarget);
						},
						style: backgroundColorButtonStyle,
					},
				]}
			/>

			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: MarkFormat.BOLD,
						label: 'Pogrubienie',
						icon: 'bold',
						active: isMarkActive(editor, MarkFormat.BOLD),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleMark(editor, MarkFormat.BOLD);
						},
					},
					{
						key: MarkFormat.ITALIC,
						label: 'Kursywa',
						icon: 'italic',
						active: isMarkActive(editor, MarkFormat.ITALIC),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleMark(editor, MarkFormat.ITALIC);
						},
					},
					{
						key: MarkFormat.UNDERLINE,
						label: 'Podkreślenie',
						icon: 'underline',
						active: isMarkActive(editor, MarkFormat.UNDERLINE),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleMark(editor, MarkFormat.UNDERLINE);
						},
					},
					{
						key: MarkFormat.STRIKETHROUGH,
						label: 'Przekreślenie',
						icon: 'strikethrough',
						active: isMarkActive(editor, MarkFormat.STRIKETHROUGH),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleMark(editor, MarkFormat.STRIKETHROUGH);
						},
					},
				]}
			/>

			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: AligmentFormat.LEFT,
						label: 'Wyrównaj do lewej',
						icon: 'align-left',
						active:
							isBlockActive(editor, AligmentFormat.LEFT) ||
							(!isBlockActive(editor, AligmentFormat.CENTER) &&
								!isBlockActive(editor, AligmentFormat.RIGHT)),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, AligmentFormat.LEFT);
						},
					},
					{
						key: AligmentFormat.CENTER,
						label: 'Wyrównaj do środka',
						icon: 'align-center',
						active: isBlockActive(editor, AligmentFormat.CENTER),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, AligmentFormat.CENTER);
						},
					},
					{
						key: AligmentFormat.RIGHT,
						label: 'Wyrównaj do prawej',
						icon: 'align-right',
						active: isBlockActive(editor, AligmentFormat.RIGHT),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, AligmentFormat.RIGHT);
						},
					},
				]}
			/>

			{/* <ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: 'floatLeft',
						label: 'Opływaj z lewej',
						icon: 'outdent',
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, FloatFormat.FLOAT_LEFT);
						},
						active: isBlockActive(editor, FloatFormat.FLOAT_LEFT),
					},
					{
						key: 'floatRight',
						label: 'Opływaj z prawej',
						icon: 'indent',
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, FloatFormat.FLOAT_RIGHT);
						},
						active: isBlockActive(editor, FloatFormat.FLOAT_RIGHT),
					},
					{
						key: 'clearBoth',
						label: 'Wstrzymaj opływanie',
						icon: 'grip-lines',
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, FloatFormat.CLEAR_BOTH);
						},
						active: isBlockActive(editor, FloatFormat.CLEAR_BOTH),
					},
				]}
			/> */}

			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: BaseBlockFormat.HEADING_ONE,
						label: 'Nagłówek 1',
						icon: () => <HeadingOne />,
						active: isBlockActive(editor, BaseBlockFormat.HEADING_ONE),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, BaseBlockFormat.HEADING_ONE);
						},
					},
					{
						key: BaseBlockFormat.HEADING_TWO,
						label: 'Nagłówek 2',
						icon: () => <HeadingTwo />,
						active: isBlockActive(editor, BaseBlockFormat.HEADING_TWO),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, BaseBlockFormat.HEADING_TWO);
						},
					},
					{
						key: BaseBlockFormat.HEADING_THREE,
						label: 'Nagłówek 3',
						icon: () => <HeadingThree />,
						active: isBlockActive(editor, BaseBlockFormat.HEADING_THREE),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, BaseBlockFormat.HEADING_THREE);
						},
					},
					{
						key: BaseBlockFormat.BLOCKQUOTE,
						label: 'Cytat',
						// icon: () => iconList.blockquote,
						icon: 'quote-right',
						iconStyle: 'solid',
						active: isBlockActive(editor, BaseBlockFormat.BLOCKQUOTE),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, BaseBlockFormat.BLOCKQUOTE);
						},
					},
				]}
			/>

			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: ListFormat.ORDERED,
						label: 'Lista numeracyjna',
						icon: 'list-ol',
						active: isBlockActive(editor, ListFormat.ORDERED),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, ListFormat.ORDERED);
						},
					},
					{
						key: ListFormat.UNORDERED,
						label: 'Lista punktowa',
						icon: 'list-ul',
						active: isBlockActive(editor, ListFormat.UNORDERED),
						onMouseDown: (event) => {
							event.preventDefault();
							toggleBlock(editor, ListFormat.UNORDERED);
						},
					},
				]}
			/>

			<InsertEmbedPopup
				ref={imageEmbedRef}
				format={EmbedBlockFormat.IMAGE}
				editor={editor}
				onOpenStateChange={(isOpen) => setWhileInsertingState((prev) => ({ ...prev, image: isOpen }))}
				uploadRequestFunction={uploadRequestFunction}
			/>
			<InsertEmbedPopup
				ref={videoEmbedRef}
				format={EmbedBlockFormat.VIDEO}
				editor={editor}
				onOpenStateChange={(isOpen) => setWhileInsertingState((prev) => ({ ...prev, video: isOpen }))}
			/>
			<InsertLinkPopup
				ref={linkPopupRef}
				editor={editor}
				onOpenStateChange={(isOpen) => setWhileInsertingState((prev) => ({ ...prev, link: isOpen }))}
			/>
			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: 'image',
						label: 'Obraz',
						icon: 'image',
						onMouseDown: (event) => {
							event.preventDefault();
							imageEmbedRef?.current?.toggle(event?.currentTarget);
						},
						active: whileInsertingState.image || isBlockActive(editor, EmbedBlockFormat.IMAGE),
					},
					{
						key: 'video',
						label: 'Wideo',
						icon: 'video',
						onMouseDown: (event) => {
							event.preventDefault();
							videoEmbedRef?.current?.toggle(event?.currentTarget);
						},
						active: whileInsertingState.video || isBlockActive(editor, EmbedBlockFormat.VIDEO),
					},
					{
						key: 'link',
						label: 'Link',
						icon: 'link',
						onMouseDown: (event) => {
							event.preventDefault();
							linkPopupRef?.current?.toggle(event?.currentTarget);
						},
						active: whileInsertingState.link || isBlockActive(editor, BaseBlockFormat.LINK),
					},
				]}
			/>

			<TableSelector
				ref={tablePopupRef}
				editor={editor}
				onOpenStateChange={(isOpen) => setWhileInsertingState((prev) => ({ ...prev, table: isOpen }))}
			/>
			<ButtonGroup
				variant="primary-outlined"
				iconOnly
				buttons={[
					{
						key: BaseBlockFormat.TWO_COLUMNS,
						label: 'Układ kolumnowy',
						icon: 'table-columns',
						onMouseDown: (event) => {
							event.preventDefault();
							insertBlock(editor, BaseBlockFormat.TWO_COLUMNS);
						},
						active: isBlockActive(editor, BaseBlockFormat.TWO_COLUMNS),
					},
					{
						key: TableFormat.TABLE,
						label: 'Tabela',
						icon: 'table',
						onMouseDown: (event) => {
							event.preventDefault();
							tablePopupRef?.current?.toggle(event?.currentTarget);
						},
						active: whileInsertingState.table || isBlockActive(editor, TableFormat.TABLE),
					},
				]}
			/>

			{/* {toolbarGroups.map((group, index) => (
				<span key={index} className="toolbar-grp">
					{group.map((element) => {
						switch (element.type) {
							case 'block':
								return <BlockButton key={element.id} editor={editor} {...element} />;
							case 'mark':
								return <MarkButton key={element.id} editor={editor} {...element} />;
							// case 'dropdown':
							// 	return <Dropdown key={element.id} editor={editor} {...element} />;
							case 'l ink':
								return (
									<LinkButton
										key={element.id}
										active={isBlockActive(editor, 'link')}
										editor={editor}
									/>
								);
							case 'embed':
								return <Embed key={element.id} format={element.format} editor={editor} />;
							// case 'color-picker':
							// 	return (
							// 		<ColorPicker
							// 			key={element.id}
							// 			activeMark={activeMark}
							// 			format={element.format}
							// 			editor={editor}
							// 		/>
							// 	);
							default:
								return null;
						}
					})}
				</span>
			))} */}
		</div>
	);
};

export default Toolbar;
